import { combineReducers, createReducer } from '@reduxjs/toolkit';
import * as actions from 'store/visitSummary/actions.common';
import {
  BinaryDocument,
  OptionType,
  ShareVisitSummaryCcd,
  VisitSummary,
  VisitSummaryDetails,
  VisitSummaryFilter,
  Selection
} from 'store/visitSummary/types';
import { Nullable, RequestState } from 'modules/types/common';
import { oc } from 'ts-optchain';
import { switchAccountSuccess } from 'store/account/actions';
import { eobDocumentsReducer, eobDownloadsReducer } from './eob/reducers';
import { EobDocumentsState, EobDownloadsState } from './eob/types';

export interface VisitSummaryListState {
  isFetchingShortList: boolean;
  isFetchingFullList: boolean;
  isFetching: boolean;
  cacheUpdatedAt: string;
  data: VisitSummary[];
  filter: {
    [key: string]: VisitSummaryFilter;
  };
  sortBy: {
    [key: string]: OptionType;
  };
  error: Nullable<Error>;
  errorShortList: Nullable<Error>;
  errorFullList: Nullable<Error>;
  isPolling: boolean;
  totalVisits: number;
  shortListRequestState: RequestState;
  fullListRequestState: RequestState;
  isSelecting: boolean;
  selected: Selection;
}

export interface VisitSummaryDetailsState {
  isFetching: boolean;
  data: VisitSummaryDetails;
  error: Nullable<Error>;
}

export interface VisitSummaryDocumentState {
  isFetching: boolean;
  data: BinaryDocument;
  error: Nullable<Error>;
}

type VisitSummaryDownloadStatus = 'pending' | 'success' | 'fail';

type VisitSummaryDownloadsState = Record<string, VisitSummaryDownloadStatus>;

export interface VisitSummaryShareCcdState {
  isSending: boolean;
  data: null | ShareVisitSummaryCcd[];
}

export interface VisitSummaryState {
  visits: VisitSummaryListState;
  activeVisitDetails: VisitSummaryDetailsState;
  downloads: Record<string, VisitSummaryDownloadStatus>;
  multipleDownloads: Record<string, VisitSummaryDownloadStatus>;
  activeVisitDocument: VisitSummaryDocumentState;
  shareCcd: VisitSummaryShareCcdState;
  multipleShareCcd: VisitSummaryShareCcdState;
  eob: EobDocumentsState;
  eobDownloads: EobDownloadsState;
}

export const visitSummaryListInitialState = (): VisitSummaryListState => ({
  isFetchingShortList: false,
  isFetchingFullList: false,
  isFetching: false,
  cacheUpdatedAt: '',
  data: [],
  sortBy: {},
  filter: {},
  error: null,
  errorShortList: null,
  errorFullList: null,
  isPolling: false,
  totalVisits: 0,
  shortListRequestState: RequestState.INITIALIZED,
  fullListRequestState: RequestState.INITIALIZED,
  isSelecting: false,
  selected: {}
});

const visitSummaryListReducer = createReducer(visitSummaryListInitialState(), {
  [switchAccountSuccess.type]: () => visitSummaryListInitialState(),
  [actions.GET_VISIT_SUMMARY_SHORT_LIST]: state => {
    state.isFetchingShortList = true;
    state.errorShortList = null;
    state.shortListRequestState = RequestState.REQUESTING;
  },
  [actions.GET_VISIT_SUMMARY_SHORT_LIST_FAIL]: (state, action) => {
    state.isFetchingShortList = false;
    state.errorShortList = action.error;
    state.shortListRequestState = RequestState.ERROR;
  },
  [actions.GET_VISIT_SUMMARY_SHORT_LIST_SUCCESS]: (state, action) => {
    state.isFetchingShortList = false;
    state.data = action.payload.data;
    state.totalVisits = Number(
      oc(action).payload.headers['x-total-visits'](action.payload.data.length)
    );
    state.shortListRequestState = RequestState.SUCCESS;
  },
  [actions.GET_VISIT_SUMMARY_FULL_LIST]: state => {
    state.isFetchingFullList = true;
    state.errorFullList = null;
    state.fullListRequestState = RequestState.REQUESTING;
  },
  [actions.GET_VISIT_SUMMARY_FULL_LIST_FAIL]: (state, action) => {
    state.isFetchingFullList = false;
    state.errorFullList = action.error;
    state.fullListRequestState = RequestState.ERROR;
  },
  [actions.GET_VISIT_SUMMARY_FULL_LIST_SUCCESS]: (state, action) => {
    state.isFetchingFullList = false;
    state.data = action.payload.data;
    if (action.payload.cacheUpdatedAt) state.cacheUpdatedAt = action.payload.cacheUpdatedAt;
    state.totalVisits = Number(
      oc(action).payload.headers['x-total-visits'](action.payload.data.length)
    );
    state.fullListRequestState = RequestState.SUCCESS;
  },
  [actions.START_POLLING]: state => {
    state.isPolling = true;
  },
  [actions.STOP_POLLING]: state => {
    state.isPolling = false;
  },
  [actions.GET_VISIT_SUMMARY_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.cacheUpdatedAt = action.payload.cacheUpdatedAt;
  },
  [actions.SET_VISIT_SUMMARY_FILTER]: (state, action) => {
    state.filter = { ...state.filter, ...action.payload };
  },
  [actions.SET_VISIT_SUMMARY_SORT_BY]: (state, action) => {
    state.sortBy = { ...state.sortBy, ...action.payload };
  },
  [actions.RESET_VISIT_SUMMARY_FILTER]: state => {
    state.filter = visitSummaryListInitialState().filter;
  },
  [actions.SET_IS_SELECTING]: (state, action) => {
    state.isSelecting = action.payload;
  },
  [actions.RESET_SELECTED]: state => {
    state.selected = {};
  },
  [actions.SET_SELECTED]: (state, action) => {
    state.selected = action.payload;
  },
  [actions.SET_SELECTED_BY_ID]: (state, action) => {
    state.selected = {
      ...state.selected,
      [action.payload]: !state.selected[action.payload]
    };
  }
});

export const visitSummaryDetailsInitialState = (): VisitSummaryDetailsState => ({
  isFetching: false,
  data: {} as VisitSummaryDetails,
  error: null
});

const visitSummaryDetailsReducer = createReducer(visitSummaryDetailsInitialState(), {
  [actions.GET_VISIT_SUMMARY_DETAILS]: state => {
    state.isFetching = true;
    state.data = {} as VisitSummaryDetails;
    state.error = null;
  },
  [actions.GET_VISIT_SUMMARY_DETAILS_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.GET_VISIT_SUMMARY_DETAILS_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = action.payload.data;
  },
  [actions.RESET_VISIT_SUMMARY_DETAILS]: () => {
    return visitSummaryDetailsInitialState();
  }
});

export const visitSummaryDownloadsReducer = createReducer<VisitSummaryDownloadsState>(
  {},
  {
    [actions.GET_VISIT_SUMMARY_CCD]: (state, action) => {
      state[action.payload.id] = 'pending';
    },
    [actions.GET_VISIT_SUMMARY_CCD_SUCCESS]: (state, action) => {
      state[action.payload.id] = 'success';
    },
    [actions.GET_VISIT_SUMMARY_CCD_FAIL]: (state, action) => {
      state[action.payload.id] = 'fail';
    }
  }
);

export const visitSummaryMultipleDownloadsReducer = createReducer<VisitSummaryDownloadsState>(
  {},
  {
    [actions.GET_MULTIPLE_VISIT_SUMMARY_CCD]: (state, action) => {
      state[action.payload.patientId] = 'pending';
    },
    [actions.GET_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS]: (state, action) => {
      state[action.payload.patientId] = 'success';
    },
    [actions.GET_MULTIPLE_VISIT_SUMMARY_CCD_FAIL]: (state, action) => {
      state[action.payload.patientId] = 'fail';
    }
  }
);

export const visitSummaryDocumentsInitialState = (): VisitSummaryDocumentState => ({
  isFetching: false,
  data: {} as BinaryDocument,
  error: null
});

const visitSummaryDocumentsReducer = createReducer(visitSummaryDocumentsInitialState(), {
  [actions.GET_VISIT_SUMMARY_DOCUMENT]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.GET_VISIT_SUMMARY_DOCUMENT_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.GET_VISIT_SUMMARY_DOCUMENT_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = action.payload.data;
  },
  [switchAccountSuccess.type]: () => visitSummaryDocumentsInitialState()
});

export const visitSummaryShareCcdInitialState: VisitSummaryShareCcdState = {
  data: [],
  isSending: false
};

export const visitSummaryMultipleShareCcdInitialState: VisitSummaryShareCcdState = {
  data: [],
  isSending: false
};

export const visitSummaryShareCcdReducer = createReducer(visitSummaryShareCcdInitialState, {
  [actions.SHARE_VISIT_SUMMARY_CCD]: state => {
    state.isSending = true;
  },
  [actions.SHARE_VISIT_SUMMARY_CCD_SUCCESS]: (state, action) => {
    state.isSending = false;
    state.data = action.payload.data;
  },
  [actions.SHARE_VISIT_SUMMARY_CCD_FAIL]: state => {
    state.isSending = false;
  }
});

export const visitSummaryMultipleShareCcdReducer = createReducer(
  visitSummaryMultipleShareCcdInitialState,
  {
    [actions.SHARE_MULTIPLE_VISIT_SUMMARY_CCD]: state => {
      state.isSending = true;
    },
    [actions.SHARE_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS]: (state, action) => {
      state.isSending = false;
      state.data = action.payload.data;
    },
    [actions.SHARE_MULTIPLE_VISIT_SUMMARY_CCD_FAIL]: state => {
      state.isSending = false;
    }
  }
);

export default combineReducers({
  visits: visitSummaryListReducer,
  activeVisitDetails: visitSummaryDetailsReducer,
  downloads: visitSummaryDownloadsReducer,
  multipleDownloads: visitSummaryMultipleDownloadsReducer,
  activeVisitDocument: visitSummaryDocumentsReducer,
  shareCcd: visitSummaryShareCcdReducer,
  multipleShareCcd: visitSummaryMultipleShareCcdReducer,
  eob: eobDocumentsReducer,
  eobDownloads: eobDownloadsReducer
});
